import { useState, useCallback } from "react";
import { Upload, X, FileText, Calendar, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDropzone } from "react-dropzone";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  kbis: "Kbis / Extrait K",
  urssaf_certificate: "Attestation URSSAF",
  rc_pro_insurance: "Assurance RC Pro",
  fiscal_regularity: "Régularité fiscale",
  decennial_insurance: "Assurance décennale",
  professional_card: "Carte professionnelle",
  qualibat: "Certification Qualibat",
  rib: "RIB",
  statutes: "Statuts",
  other: "Autre document",
};

// Default validity periods in months
const DEFAULT_VALIDITY: Record<string, number> = {
  kbis: 3,
  urssaf_certificate: 6,
  rc_pro_insurance: 12,
  fiscal_regularity: 12,
  decennial_insurance: 12,
};

interface PortalDocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  documentType: string | null;
  onSuccess: () => void;
}

const PortalDocumentUploadDialog = ({
  open,
  onOpenChange,
  token,
  documentType,
  onSuccess,
}: PortalDocumentUploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [validFrom, setValidFrom] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      
      // Auto-set dates based on document type
      const today = new Date();
      setValidFrom(today.toISOString().split("T")[0]);
      
      if (documentType && DEFAULT_VALIDITY[documentType]) {
        const expiry = new Date();
        expiry.setMonth(expiry.getMonth() + DEFAULT_VALIDITY[documentType]);
        setExpiresAt(expiry.toISOString().split("T")[0]);
      }
    }
  }, [documentType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".webp"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleSubmit = async () => {
    if (!file || !documentType) return;

    try {
      setUploading(true);

      // First, get subcontractor info to get the owner's user_id
      const { data: subData, error: subError } = await supabase.rpc(
        "get_subcontractor_by_token",
        { p_token: token }
      );

      if (subError) throw subError;
      if (!subData || subData.length === 0) {
        throw new Error("Token invalide");
      }

      const subcontractorId = subData[0].id;

      // Upload file to a public path (portal uploads)
      const fileExt = file.name.split(".").pop();
      const fileName = `portal/${subcontractorId}/${Date.now()}_${documentType}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Call the portal upload function
      const { error: insertError } = await supabase.rpc(
        "portal_upload_subcontractor_document",
        {
          p_token: token,
          p_document_type: documentType as any,
          p_document_id: null, // We don't create a document record from portal
          p_valid_from: validFrom || null,
          p_expires_at: expiresAt || null,
          p_label: `${DOCUMENT_TYPE_LABELS[documentType] || documentType} - ${file.name}`,
        }
      );

      if (insertError) throw insertError;

      toast({
        title: "Document déposé avec succès",
        description: "Votre document sera vérifié prochainement.",
      });

      onOpenChange(false);
      setFile(null);
      setValidFrom("");
      setExpiresAt("");
      onSuccess();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Erreur lors du dépôt",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      onOpenChange(false);
      setFile(null);
      setValidFrom("");
      setExpiresAt("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Déposer : {documentType ? DOCUMENT_TYPE_LABELS[documentType] : "Document"}
          </DialogTitle>
          <DialogDescription>
            Téléversez votre document au format PDF ou image (max 10 Mo)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
              isDragActive && "border-primary bg-primary/5",
              file && "border-emerald-500 bg-emerald-500/5"
            )}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-sm truncate max-w-[200px]">
                  {file.name}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {isDragActive
                    ? "Déposez le fichier ici"
                    : "Glissez-déposez ou cliquez pour sélectionner"}
                </p>
              </>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="validFrom" className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Date de début
              </Label>
              <Input
                id="validFrom"
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiresAt" className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Date d'expiration
              </Label>
              <Input
                id="expiresAt"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={!file || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Déposer
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortalDocumentUploadDialog;
